import { useEffect, useMemo } from 'react';
import { Badge, Button } from 'react-bootstrap';
// import { AiOutlineDelete } from 'react-icons/ai';
import { FaEye, FaUserSlash, FaRegEdit } from 'react-icons/fa';
import { useIntl, FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { usePagination, useRowSelect, useTable, useFilters, useExpanded } from 'react-table';
import DefaultColumnFilter from '../../../../Components/ReactTable/Filters/DefaultColumnFilter';
import ReactTableRender from '../../../../Components/ReactTable/ReactTableRender';
import { clearTable, handleCdAdminListChange } from '../../../../Redux/slices/table';
import Swal from 'sweetalert2';
import { Link } from 'react-router-dom';
import { fetchWrapper } from '../../../../_helpers/fetchWrapper';
import { ErrorMessageHandler } from '../../../../_helpers/_methods';
import { toast } from 'react-toastify';
import UserImage from '../../../../Components/UserImage';

function CdAdminTable() {
  const intl = useIntl();
  const dispatch = useDispatch();
  const pageNum = useSelector((state) => state.table.pageNum);
  const loading = useSelector((state) => state.table.loading);
  const totalCount = useSelector((state) => state.table.totalCount);
  const permissions = useSelector((state) => state.user.permissions);
  // const onDeleteClick = (id) => {
  //   Swal.fire({
  //     title: 'Are you sure?',
  //     text: "You won't be able to revert this!",
  //     icon: 'warning',
  //     showCancelButton: true,
  //     confirmButtonColor: '#3085d6',
  //     cancelButtonColor: '#d33',
  //     confirmButtonText: 'Yes, delete it!'
  //   }).then((result) => {
  //     if (result.isConfirmed) {
  //       handleDelete(id);
  //     }
  //   });
  // };
  const handleSuspend = (original) => {
    Swal.fire({
      title: intl.formatMessage({ id: 'are_you_sure' }),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: intl.formatMessage({ id: 'yes' }),
      cancelButtonText: intl.formatMessage({ id: 'cancel' }),
      reverseButtons: true
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          if (original.suspended) {
            await fetchWrapper.get(`/profile/cd-admin/${original.id}/unsuspend/`);
            toast.success(<FormattedMessage id="unsuspended_successfully" />);
          } else {
            await fetchWrapper.get(`/profile/cd-admin/${original.id}/suspend/`);
            toast.success(<FormattedMessage id="suspended_successfully" />);
          }
          dispatch(handleCdAdminListChange({ index: pageIndex, pageSize: pageSize }));
        } catch (error) {
          ErrorMessageHandler(error);
        }
      }
    });
  };
  const results = useSelector((state) => state.table.results);
  const columns = useMemo(
    () => [
      {
        Header: <FormattedMessage id="userFullName" />,
        accessor: 'fullname',
        Cell: ({ row: { original }, cell: { value } }) => {
          return (
            <Link to={`profile/${original.id}`}>
              <span className="d-flex align-items-center ">
                <div className="me-1 position-relative">
                  <UserImage
                    avatar={original.image}
                    userName={value}
                    userId={String(original.id)}
                  />
                </div>

                <span className="d-block">
                  {value} <br />
                </span>
                {original.suspended && (
                  <Badge bg="danger">
                    <FormattedMessage id="suspended" />
                  </Badge>
                )}
              </span>
            </Link>
          );
        }
      },
      {
        Header: <FormattedMessage id="office_type" />,
        accessor: 'office_type'
      },
      {
        Header: <FormattedMessage id="province" />,
        accessor: 'province'
      },
      {
        Header: <FormattedMessage id="mobile_number" />,
        accessor: 'phone'
      },
      {
        Header: <FormattedMessage id="actions" />,
        accessor: 'actions',
        disableFilters: true,
        Cell: ({ row: { original } }) => (
          <div className="d-flex">
            <div>
              <Link to={`profile/${original.id}`}>
                <Button variant="outline-info">
                  <FaEye />
                </Button>
              </Link>
            </div>

            {permissions['Can change cd admin'] && (
              <>
                <div className="ms-2">
                  <Link to={`edit/${original.id}`}>
                    <Button variant="outline-secondary ">
                      <FaRegEdit />
                    </Button>
                  </Link>
                </div>
                <div className="ms-2">
                  <Button
                    variant="outline-danger "
                    className="d-flex justify-content-center align-items-center"
                    onClick={() => handleSuspend(original)}
                    style={{ minWidth: '150px' }}>
                    <FaUserSlash className="me-2" />
                    {original.suspended ? (
                      <FormattedMessage id="unsuspend" />
                    ) : (
                      <FormattedMessage id="suspend" />
                    )}
                  </Button>
                </div>
              </>
            )}
          </div>
        )
      }
    ],
    [permissions]
  );

  const data = useMemo(() => {
    if (!results) return [];
    return results.map((result) => {
      return {
        fullname: result.fullname,
        office_type: result.office_type,
        province: result.province?.title,
        phone: result.phone,
        id: result.id,
        suspended: result.suspended
      };
    });
  }, [results]);

  const defaultColumn = useMemo(
    () => ({
      // Let's set up our default Filter UI
      Filter: DefaultColumnFilter
    }),
    []
  );

  const {
    headerGroups,
    getTableProps,
    getTableBodyProps,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    visibleColumns,
    setPageSize,
    state: { pageSize, pageIndex }
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0, pageSize: 50 },
      manualPagination: true,
      pageCount: pageNum,
      defaultCanFilter: false,
      defaultColumn
      // manualFilters: true
    },
    useExpanded,
    useFilters,
    usePagination,
    useRowSelect
  );
  const pageCountArray = useMemo(
    () => Array.from({ length: Math.ceil(totalCount / pageSize) }, (v, i) => i + 1),
    [totalCount]
  );

  useEffect(() => {
    dispatch(handleCdAdminListChange({ index: pageIndex, pageSize: pageSize }));
  }, [pageIndex, pageSize]);
  useEffect(() => {
    return () => {
      dispatch(clearTable());
    };
  }, []);
  return (
    <div>
      <ReactTableRender
        getTableProps={getTableProps}
        headerGroups={headerGroups}
        getTableBodyProps={getTableBodyProps}
        page={page}
        prepareRow={prepareRow}
        totalCount={totalCount}
        gotoPage={gotoPage}
        previousPage={previousPage}
        pageCount={pageCount}
        pagination={true}
        pageCountArray={pageCountArray}
        pageNum={pageNum}
        showGoto={true}
        nextPage={nextPage}
        pageIndex={pageIndex}
        canNextPage={canNextPage}
        canPreviousPage={canPreviousPage}
        loading={loading}
        visibleColumns={visibleColumns}
        pageSize={pageSize}
        setPageSize={setPageSize}
      />
    </div>
  );
}

export default CdAdminTable;
